<template>
  <div class="valid-orders-page">
    <el-card shadow="never" class="search-card">
      <el-form :inline="true" :model="searchForm" class="search-form">
        <el-form-item label="用户手机号">
          <el-input
            v-model="searchForm.userPhone"
            placeholder="请输入手机号"
            clearable
            maxlength="11"
            style="width: 180px"
          />
        </el-form-item>
        <el-form-item label="订单编号">
          <el-input
            v-model="searchForm.orderId"
            placeholder="请输入订单编号"
            clearable
            style="width: 180px"
          />
        </el-form-item>
        <el-form-item label="分佣时间">
          <el-date-picker
            v-model="commissionTimeRange"
            type="datetimerange"
            range-separator="-"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
            value-format="YYYY-MM-DD HH:mm"
            format="YYYY-MM-DD HH:mm"
            :default-time="[new Date(2000, 0, 1, 0, 0, 0), new Date(2000, 0, 1, 23, 59, 59)]"
            style="width: 360px"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :icon="Search" @click="handleQuery">查询</el-button>
          <el-button :icon="Refresh" @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card shadow="never" class="table-card">
      <el-table :data="list" v-loading="loading" border style="width: 100%">
        <el-table-column type="index" label="序号" width="60" align="center" :index="(i) => i + 1" />
        <el-table-column prop="billUserNickname" label="用户昵称" min-width="100" show-overflow-tooltip>
          <template #default="{ row }">{{ row.billUserNickname ?? '--' }}</template>
        </el-table-column>
        <el-table-column prop="billUserCode" label="用户ID" width="100" align="center">
          <template #default="{ row }">{{ row.billUserCode ?? row.billUserId ?? '--' }}</template>
        </el-table-column>
        <el-table-column prop="billUserPhone" label="用户手机号" width="120" align="center">
          <template #default="{ row }">{{ row.billUserPhone ?? '--' }}</template>
        </el-table-column>
        <el-table-column prop="orderId" label="订单编号" width="160" show-overflow-tooltip>
          <template #default="{ row }">{{ row.orderId ?? '--' }}</template>
        </el-table-column>
        <el-table-column label="订单总价" width="100" align="right">
          <template #default="{ row }">¥{{ formatMoney(row.orderTotalAmount) }}</template>
        </el-table-column>
        <el-table-column prop="spuName" label="分佣商品" min-width="140" show-overflow-tooltip>
          <template #default="{ row }">{{ row.spuName ?? '--' }}</template>
        </el-table-column>
        <el-table-column label="商品单价" width="100" align="right">
          <template #default="{ row }">¥{{ formatMoney(row.sellingPrice) }}</template>
        </el-table-column>
        <el-table-column prop="totalQuantity" label="商品数量" width="90" align="center">
          <template #default="{ row }">{{ row.totalQuantity ?? '--' }}</template>
        </el-table-column>
        <el-table-column prop="splitTime" label="分佣时间" width="170" align="center">
          <template #default="{ row }">{{ row.splitTime ?? '--' }}</template>
        </el-table-column>
        <el-table-column label="分佣金额" width="100" align="right">
          <template #default="{ row }">¥{{ formatMoney(row.splitAmount) }}</template>
        </el-table-column>
        <el-table-column label="直接粉丝ID" width="100" align="center">
          <template #default="{ row }">{{ row.directFanUserCode ?? row.directFanUserId ?? '--' }}</template>
        </el-table-column>
        <el-table-column prop="directFanNickname" label="直接粉丝昵称" min-width="100" show-overflow-tooltip>
          <template #default="{ row }">{{ row.directFanNickname ?? '--' }}</template>
        </el-table-column>
        <el-table-column label="间接粉丝ID" width="100" align="center">
          <template #default="{ row }">{{ row.indirectFanUserCode ?? row.indirectFanUserId ?? '--' }}</template>
        </el-table-column>
        <el-table-column prop="indirectFanNickname" label="间接粉丝昵称" min-width="100" show-overflow-tooltip>
          <template #default="{ row }">{{ row.indirectFanNickname ?? '--' }}</template>
        </el-table-column>
      </el-table>

      <div class="pagination-wrap">
        <el-pagination
          v-model:current-page="pagination.pageNum"
          v-model:page-size="pagination.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="pagination.total"
          layout="total, sizes, prev, pager, next"
          @size-change="fetchList"
          @current-change="fetchList"
        />
      </div>
    </el-card>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, watch } from 'vue'
import { useCurrentAppInstance } from '@zto/zpage'
import { Search, Refresh } from '@element-plus/icons-vue'
import { yuanMoney } from '@/utils/formatter'

const app = useCurrentAppInstance()
const route = app.useRoute()

/** 从 URL query 解析 userPhone（佣金记录等入口跳转带入） */
function readPhoneFromQuery () {
  const q = route.query.userPhone
  if (typeof q === 'string') return q.trim()
  if (Array.isArray(q) && q[0]) return String(q[0]).trim()
  return ''
}

function applyPhoneFromRoute () {
  const phone = readPhoneFromQuery()
  if (phone) searchForm.userPhone = phone
}

const searchForm = reactive({
  userPhone: '',
  orderId: ''
})
const commissionTimeRange = ref<[string, string] | null>(null)
const loading = ref(false)
const list = ref<any[]>([])
const pagination = reactive({
  pageNum: 1,
  pageSize: 10,
  total: 0
})

function formatMoney (val: number | string | null | undefined): string {
  if (val == null || val === '') return '0.00'
  const n = Number(val)
  if (Number.isNaN(n)) return '0.00'
  return yuanMoney(n, { decimals: 2, thousandSep: ',', emptyText: '0.00' })
}

function buildParams () {
  const [start, end] = commissionTimeRange.value || []
  return {
    userPhone: (searchForm.userPhone || '').trim() || undefined,
    orderId: (searchForm.orderId || '').trim() || undefined,
    commissionTimeStart: start || undefined,
    commissionTimeEnd: end || undefined,
    pageNum: pagination.pageNum,
    pageSize: pagination.pageSize
  }
}

async function fetchList () {
  loading.value = true
  try {
    const params = buildParams()
    const res: any = await app.request({
      api: '/admin/dataversion/validSettleOrderList',
      method: 'POST',
      data: params
    })
    if (res && typeof res === 'object') {
      list.value = Array.isArray(res.list) ? res.list : []
      pagination.total = Number(res.totalCount) || 0
    } else {
      list.value = []
      pagination.total = 0
    }
  } catch (e) {
    console.error('validSettleOrderList error', e)
    list.value = []
    pagination.total = 0
  } finally {
    loading.value = false
  }
}

function handleQuery () {
  pagination.pageNum = 1
  fetchList()
}

function handleReset () {
  searchForm.userPhone = ''
  searchForm.orderId = ''
  commissionTimeRange.value = null
  pagination.pageNum = 1
  fetchList()
}

onMounted(() => {
  applyPhoneFromRoute()
  fetchList()
})

watch(
  () => route.query.userPhone,
  () => {
    applyPhoneFromRoute()
    pagination.pageNum = 1
    fetchList()
  }
)
</script>

<style lang="scss" scoped>
.valid-orders-page {
  padding: 16px 24px;
  background: #f5f7fa;
  min-height: 100%;
}

.search-card {
  margin-bottom: 16px;
  border-radius: 8px;

  .search-form {
    margin-bottom: 0;

    :deep(.el-form-item) {
      margin-right: 24px;
    }
  }
}

.table-card {
  border-radius: 8px;
}

.pagination-wrap {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}
</style>
